import React, { useCallback, useEffect, useState } from 'react';
import FlowStrip from '../../help/FlowStrip';
import StatusStagesChip from '../../help/StatusStagesChip';
import { salesFlow } from '../../help/flows';
import { CHALLAN_STAGES } from '../../help/statusStages';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Table, TableBody,
  TableCell, TableHead, TableRow, TextField, Typography,
  Tab, Tabs, // F5
} from '@mui/material';
import AttachmentsPanel from '../common/AttachmentsPanel';
import SerialPostDialog from '../serial/SerialPostDialog'; // F6b
import DocumentSerialsCaption from '../serial/DocumentSerialsCaption';
import { useDocumentSerials } from '../serial/useDocumentSerials';
import { DeliveryChallanService } from '../../services/salesOrder.service';
import { CHALLAN_STATUS_COLORS, Challan } from '../../interfaces/sales/salesOrder.interface';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { formatMoney, humanizeEnum } from '../../utils/format';
import { formatDate } from '../../utils/dateUtils';
import ChallanFulfilmentTab from './fulfilment/ChallanFulfilmentTab'; // F5

const DeliveryChallanDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const canIssue = usePermission(PERMISSIONS.STOCK_ISSUE);
  const canManage = usePermission(PERMISSIONS.SALES_ORDER_MANAGE);
  const canReturn = usePermission(PERMISSIONS.SALES_RETURN_MANAGE); // B4-D
  const [c, setC] = useState<Challan | null>(null);
  const [busy, setBusy] = useState(false);
  const [postOpen, setPostOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [searchParams] = useSearchParams(); // F5: ?tab=fulfilment opens the packages & shipment tab
  const [tab, setTab] = useState(searchParams.get('tab') === 'fulfilment' ? 1 : 0); // F5: 0 = lines, 1 = packages & shipment
  const lineSerials = useDocumentSerials(c?.id, 'SALE_DELIVERY', c?.status === 'POSTED'); // F6b: serials moved, per line

  const load = useCallback(async () => {
    try { setC(await DeliveryChallanService.get(id!)); } catch (e) { toast.showApiError(e, 'Failed to load the challan'); }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const run = async (fn: () => Promise<unknown>, ok: string, fail: string): Promise<boolean> => {
    try { setBusy(true); await fn(); toast.showSuccess(ok); await load(); return true; } catch (e) { toast.showApiError(e, fail); return false; } finally { setBusy(false); }
  };
  const pdf = async () => {
    try {
      const blob = await DeliveryChallanService.downloadPdf(id!);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.showApiError(e, 'Failed to download the PDF'); }
  };

  if (!c) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;
  const canCancel = c.status === 'DRAFT' ? (canIssue || canManage) : (c.status === 'POSTED' && canIssue);

  return (
    <Box sx={{ p: 3 }} data-help-page="sales.challan.page">
      <FlowStrip data-help="sales.challan.flow" stages={salesFlow({ current: 'challan', order: { id: c.salesOrderId, no: c.orderNo }, challans: [{ id: c.id, no: c.challanNo ?? 'Draft' }], invoices: [{ id: c.salesInvoiceId, no: c.invoiceNumber }], journalEntryId: c.journalEntryId })} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        <Typography variant="h5">Delivery Challan{c.challanNo ? ` ${c.challanNo}` : ''} <StatusStagesChip data-help="sales.challan.statusStages" label={humanizeEnum(c.status)} status={c.status} stages={CHALLAN_STAGES} color={CHALLAN_STATUS_COLORS[c.status]} /></Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button data-help="sales.challan.order" onClick={() => navigate(`/sales/orders/${c.salesOrderId}`)}>Order {c.orderNo}</Button>
          {c.status !== 'DRAFT' && <Button data-help="sales.challan.pdf" onClick={pdf}>PDF</Button>}
          {canReturn && c.status === 'POSTED' && <Button data-help="sales.challan.recordReturn" onClick={() => navigate(`/sales/challans/${c.id}/return`)}>Record return</Button>}
          {canIssue && c.status === 'DRAFT' && <Button variant="contained" data-help="sales.challan.post" disabled={busy} onClick={() => setPostOpen(true)}>Post (issue stock)</Button>}
          {canCancel && c.status !== 'CANCELLED' && <Button color="error" data-help="sales.challan.cancel" onClick={() => setCancelOpen(true)}>Cancel</Button>}
        </Box>
      </Box>
      {c.status === 'POSTED' && c.salesInvoiceId && <Alert severity="info" sx={{ mb: 1 }}>On {c.invoiceNumber ?? 'a draft sales invoice'}. Void or delete that invoice before cancelling this challan.</Alert>}
      {c.status === 'CANCELLED' && c.cancelReason && <Alert severity="info" sx={{ mb: 1 }}>Cancelled: {c.cancelReason}</Alert>}
      <Paper sx={{ p: 2, mb: 3, display: 'flex', flexWrap: 'wrap', gap: 4 }} data-help-section="sales.challan.summary">
        <Typography>Customer: <b>{c.customerName}</b></Typography>
        <Typography>Date: <b>{formatDate(c.challanDate)}</b></Typography>
        <Typography>Store: <b>{c.storeName}</b></Typography>
        {c.vehicleNo && <Typography>Vehicle: <b>{c.vehicleNo}</b></Typography>}
        {c.driverName && <Typography>Driver: <b>{c.driverName}</b></Typography>}
      </Paper>
      {/* ---- F5: lines / packages & shipment tabs ---- */}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Lines" data-help="sales.challan.tab.lines" />
        <Tab label="Packages & shipment" data-help="sales.challan.tab.fulfilment" />
      </Tabs>
      {tab === 1 && <Box sx={{ mb: 3 }}><ChallanFulfilmentTab challanId={c.id} /></Box>}
      {tab === 0 && <Paper sx={{ p: 2, mb: 3 }} data-help-section="sales.challan.lines">
        <Table size="small">
          <TableHead><TableRow><TableCell data-help="sales.challan.col.item">Item</TableCell><TableCell data-help="sales.challan.col.description">Description</TableCell><TableCell align="right" data-help="sales.challan.col.quantity">Quantity</TableCell><TableCell align="right" data-help="sales.challan.col.relieved">Stock cost relieved</TableCell></TableRow></TableHead>
          <TableBody>
            {c.lines.map(l => (
              <React.Fragment key={l.id}>
              <TableRow>
                <TableCell>
                  {l.itemCode ? `${l.itemCode} - ${l.itemName}` : 'Service'}
                  {l.itemId && <DocumentSerialsCaption serials={lineSerials[l.itemId]} />}
                </TableCell>
                <TableCell>{l.description}</TableCell>
                <TableCell align="right">{l.quantity}{l.unit ? ` ${l.unit}` : ''}</TableCell>
                <TableCell align="right">{c.status === 'DRAFT' ? '-' : formatMoney(l.relievedValue)}</TableCell>
              </TableRow>
              {(l.kitRelief ?? []).map(k => (
                <TableRow key={`${l.id}-${k.componentItemId}`} data-testid="kit-relief-row">
                  <TableCell sx={{ pl: 4, color: 'text.secondary' }}>↳ {k.code ? `${k.code} - ${k.name ?? ''}` : 'Component'}</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>
                    Kit component
                    {(k.lots ?? []).length > 0 && (
                      <Typography variant="caption" component="div" data-testid="kit-relief-lots">
                        {(k.lots ?? []).map(lot => `${lot.lotNo != null ? `Lot ${lot.lotNo}` : 'Lot'}: ${lot.quantity}${k.unit ? ` ${k.unit}` : ''}${lot.expiryDate ? ` (exp ${formatDate(lot.expiryDate)})` : ''}`).join(', ')}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary' }}>{k.quantity}{k.unit ? ` ${k.unit}` : ''}</TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary' }}>{formatMoney(k.relievedValue)}</TableCell>
                </TableRow>
              ))}{/* F6c: kit relief detail */}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </Paper>}
      {/* ---- end F5 ---- */}
      <AttachmentsPanel entityType="DELIVERY_CHALLAN" entityId={c.id} canManage={canManage || canIssue} />
      {/* F6b: serial-tracked lines pick one IN_STOCK serial per unit from the challan's store */}
      <SerialPostDialog open={postOpen} title="Post delivery challan" confirmLabel="Post" submitting={busy}
        message="This takes the goods out of the store (FIFO) and posts the cost of goods sold. It cannot be edited afterwards, only cancelled."
        lines={c.lines.map(l => ({ lineId: l.id, itemId: l.itemId, label: l.itemName ?? l.description, quantity: Number(l.quantity), mode: 'pick' as const, storeId: c.storeId }))}
        onCancel={() => setPostOpen(false)} onConfirm={async (serials) => { if (await run(() => DeliveryChallanService.post(c.id, serials), 'Challan posted', 'Failed to post the challan')) setPostOpen(false); }} />
      <Dialog open={cancelOpen} onClose={() => setCancelOpen(false)} fullWidth maxWidth="xs" data-help-page="sales.challan.cancel.page">
        <DialogTitle>Cancel challan</DialogTitle>
        <DialogContent data-help-section="sales.challan.cancel.section.form" sx={{ pt: 1 }}><TextField data-help="sales.challan.cancel.reason" label="Reason" fullWidth value={reason} onChange={e => setReason(e.target.value)} /></DialogContent>
        <DialogActions>
          <Button data-help="sales.challan.cancel.back" onClick={() => setCancelOpen(false)}>Back</Button>
          <Button color="error" data-help="sales.challan.cancel.confirm" disabled={!reason.trim() || busy} onClick={() => { setCancelOpen(false); run(() => DeliveryChallanService.cancel(c.id, reason), 'Challan cancelled', 'Failed to cancel the challan'); }}>Cancel challan</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DeliveryChallanDetail;
